import { validationErrors } from "../constants/validationErrors";
import { emptyStringValidation } from "./helpers";

const { emptyStringError } = validationErrors;

// Helper class to handle the drug response fields
class DrugResponseItems {
  constructor(dataArray) {
    this.totalSize = dataArray.length;
    this.items = dataArray.map((x) => ({
      touched: {
        db: false,
        id: false,
        drugName: false,
        condition: x.condition.map(() => ({
          db: false,
          id: false,
          name: false,
        })),
      },
      errors: {
        db: x.db || x.drugName ? false : emptyStringError,
        id: x.id || x.drugName ? false : emptyStringError,
        drugName: x.drugName || (x.id && x.db) ? false : emptyStringError,
        condition: x.condition.map((cod) => ({
          db: cod.db || cod.name ? false : emptyStringError,
          id: cod.id || cod.name ? false : emptyStringError,
          name: cod.name || (cod.id && cod.db) ? false : emptyStringError,
        })),
      },
      values: {
        db: x.db || "",
        id: x.id || "",
        drugName: x.drugName || "",
        condition: x.condition.map((cod) => ({
          db: cod.db || "",
          id: cod.id || "",
          name: cod.name || "",
        })),
      },
    }));
  }

  setItems(e, index, key, conditionIndex) {
    this.setValue(e, index, key, conditionIndex);
  }

  setValue(e, index, key, conditionIndex) {
    const { value, name } = e.target;
    if (key) {
      this.items[index].values[key][conditionIndex][name] = value;
    } else {
      this.items[index].values[name] = value;
    }
    this.setError(e, index, key, conditionIndex);
    this.normilizeErrors(e, index, key, conditionIndex);
  }

  setTouched(e, index, key, conditionIndex) {
    const { name } = e.target;
    if (key) {
      this.items[index].touched[key][conditionIndex][name] = true;
    } else {
      this.items[index].touched[name] = true;
    }
  }

  setError(e, index, key, conditionIndex) {
    const { value, name } = e.target;
    const error = emptyStringValidation(value);
    if (error) {
      if (key) {
        this.items[index].errors[key][conditionIndex][name] = error;
        return;
      }
      this.items[index].errors[name] = error;
    } else {
      if (key) {
        this.items[index].errors[key][conditionIndex][name] = false;
        return;
      }
      this.items[index].errors[name] = false;
    }
  }

  normilizeErrors(e, index, key, conditionIndex) {
    const { name, value } = e.target;
    if (key) {
      if (name === "name") {
        if (value) {
          this.items[index].errors[key][conditionIndex].db = false;
          this.items[index].errors[key][conditionIndex].id = false;
          this.items[index].errors[key][conditionIndex].name = false;
        } else {
          this.items[index].errors[key][conditionIndex].name = emptyStringError;
        }
      }
      if (name === "db" && value) {
        this.items[index].errors[key][conditionIndex].name = false;
      }
      if (name === "id" && value) {
        this.items[index].errors[key][conditionIndex].name = false;
      }
    } else {
      if (name === "drugName") {
        if (value) {
          this.items[index].errors.db = false;
          this.items[index].errors.id = false;
          this.items[index].errors.drugName = false;
        } else {
          this.items[index].errors.drugName = emptyStringError;
        }
      }
      if (name === "db" && value && this.items[index].values.id === "") {
        this.items[index].errors.id = emptyStringError;
        this.items[index].errors.drugName = false;
      }
      if (name === "id" && value && this.items[index].values.db === "") {
        this.items[index].errors.db = emptyStringError;
        this.items[index].errors.drugName = false;
      }
    }
  }

  // Check if any item has errors
  hasErrors() {
    const hasDrugResponseErrors =
      this.items.filter(({ errors }) => {
        return errors.db || errors.id || errors.drugName;
      }).length > 0;

    const hasConditionErrors = this.items
      .map(({ errors }) => errors.condition)
      .flat()
      .some((error) => error.db || error.id || error.name);
    return hasDrugResponseErrors || hasConditionErrors;
  }

  setAllTouched() {
    this.items.forEach(({ touched }) => {
      const { condition } = touched;
      touched.db = true;
      touched.id = true;
      touched.drugName = true;
      condition.forEach((cod) => {
        cod.name = true;
        cod.id = true;
        cod.db = true;
      });
    });
  }

  // Check if specific item has errors
  getItemErrors(index) {
    const {
      db: databaseError,
      id: idError,
      drugName: drugNameError,
    } = this.items[index].errors;
    return {
      databaseError,
      idError,
      drugNameError,
    };
  }

  getConditionErrors(index, conditionIndex) {
    const { condition: conditionErrors } = this.items[index].errors;
    const {
      db: conditionDbError,
      id: conditionIdError,
      name: conditionNameError,
    } = conditionErrors[conditionIndex];
    return {
      conditionDbError,
      conditionIdError,
      conditionNameError,
    };
  }

  getItemTouched(index) {
    const {
      db: databaseTouched,
      id: idTouched,
      drugName: drugNameTouched,
    } = this.items[index].touched;
    return {
      databaseTouched,
      idTouched,
      drugNameTouched,
    };
  }

  getConditionTouched(index, conditionIndex) {
    const { condition: conditionTouchedState } = this.items[index].touched;
    const {
      db: conditionDbTouched,
      id: conditionIdTouched,
      name: conditionNameTouched,
    } = conditionTouchedState[conditionIndex];
    return {
      conditionDbTouched,
      conditionIdTouched,
      conditionNameTouched,
    };
  }

  addCondition(index) {
    const { values, errors, touched } = this.items[index];
    values.condition.push({
      db: false,
      id: false,
      name: false,
    });
    errors.condition.push({
      db: emptyStringError,
      id: emptyStringError,
      name: emptyStringError,
    });
    touched.condition.push({
      db: false,
      id: false,
      name: false,
    });
  }

  removeCondition(index, secondaryIndex) {
    const { values, errors, touched } = this.items[index];
    values.condition.splice(secondaryIndex, 1);
    touched.condition.splice(secondaryIndex, 1);
    errors.condition.splice(secondaryIndex, 1);
  }

  addItem() {
    this.items.push({
      touched: {
        db: false,
        id: false,
        drugName: false,
        condition: [
          {
            db: false,
            id: false,
            name: false,
          },
        ],
      },
      errors: {
        db: emptyStringError,
        id: emptyStringError,
        drugName: emptyStringError,
        condition: [
          {
            db: emptyStringError,
            id: emptyStringError,
            name: emptyStringError,
          },
        ],
      },
      values: {
        db: "",
        id: "",
        drugName: "",
        condition: [
          {
            db: "",
            id: "",
            name: "",
          },
        ],
      },
    });
  }

  removeItem(index) {
    this.items.splice(index, 1);
  }
}

export default DrugResponseItems;
